import { Tooltip } from "@mui/material";
import { useAgentCreditUsage } from "../hooks/useAgents";
import { formatWeeklyReset } from "../../WeeklyUsage/weeklyReset";

/** From 80% of the limit the ring turns amber as a heads-up. */
const NEAR_LIMIT_PCT = 80;

/** The org's weekly usage limit, read at the foot of the left sidebar while
 * the person is in chat or the agents product, both of which spend it and are
 * paused by it (#13284; #11921, Hunter; Claude
 * Design "Credits Readout C"): status at the shell's edge, the way the Claude
 * app keeps its plan line, and out of every topbar's verb cluster where the
 * ring read as a control. One line, "Usage 34%", in the sidebar's own type
 * (Hunter, 2026-09-08: no second line, no mono); the reset and the words live
 * in the tooltip. Collapsed rail: the ring alone. The colour ladder is the
 * escalation: the nav's gray under 80%, amber from 80%, red at the cap.
 *
 * Renders nothing when the org has no limit (no denominator), and fetches
 * only while mounted, so a page outside chat and agents never asks. */
export function AgentCreditFoot({
  collapsed = false,
}: {
  collapsed?: boolean;
}) {
  const { usage } = useAgentCreditUsage();
  if (!usage) return null;
  return (
    <CreditFoot
      pct={usage.pct}
      atLimit={usage.at_limit}
      nextReset={usage.next_reset}
      collapsed={collapsed}
    />
  );
}

/** The presentational half, so the states can be drawn without a fetch. */
export function CreditFoot({
  pct: serverPct,
  atLimit,
  nextReset,
  collapsed = false,
}: {
  /** Of the weekly limit, as the server capped it. Null = unlimited → render
   *  nothing. The counts behind it never reach the foot: the server can hide
   *  them (#13953), and the foot never showed them (#11731). */
  pct: number | null;
  /** Whether a new turn is refused right now: the send gate's own answer. */
  atLimit: boolean;
  /** ISO instant of the upcoming reset; the tooltip shows it in local time.
   *  Absent, the tooltip names no day: the data did not supply one. */
  nextReset?: string | null;
  collapsed?: boolean;
}) {
  if (serverPct == null) return null;

  // The label and the ring cap at 100% — an in-flight overshoot past the
  // limit still reads as "100%", never more.
  const pct = Math.min(100, Math.max(0, Math.round(serverPct)));
  const ringRatio = pct / 100;
  const nearLimit = pct >= NEAR_LIMIT_PCT;

  // Percentage only — never the underlying counts, which would hand the
  // reader our cost basis (#11731). "Usage" and "usage limit" (Hunter,
  // 2026-09-08): never "credits" or "allowance".
  const tip =
    `Your organization has used ${pct}% of its weekly usage limit.` +
    (nextReset ? ` Resets ${formatWeeklyReset(nextReset)}.` : "") +
    (atLimit ? " New chats and agent runs are paused until the reset." : "");
  const label = `Usage ${pct}%`;
  // The colour carries the escalation for sighted readers; the accessible
  // name carries it for everyone else.
  const accessibleName = atLimit
    ? `${label}, at the limit`
    : nearLimit
      ? `${label}, near the limit`
      : label;

  // On the nav's ink (#12209): soft white at rest, amber near
  // the limit, red at it. The same three colours drive the ring and the
  // text.
  const fill = atLimit
    ? "#F26B5E"
    : nearLimit
      ? "#E0A63A"
      : "rgba(230,228,223,0.6)";
  const textColor = atLimit
    ? "text-[#F26B5E]"
    : nearLimit
      ? "text-[#E0A63A]"
      : "text-[#e6e4df]/60";

  const size = 16;
  const r = 6;
  const c = 2 * Math.PI * r;
  const ring = (
    // flex-none: the hidden nowrap label cannot shrink, so without it flexbox
    // shrinks the ring to nothing inside the rail's 40px box (#14182).
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="flex-none"
    >
      <circle
        cx="8"
        cy="8"
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="2.5"
      />
      <circle
        cx="8"
        cy="8"
        r={r}
        fill="none"
        stroke={fill}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - ringRatio)}
        transform="rotate(-90 8 8)"
        data-usage-ring=""
      />
    </svg>
  );

  // One element in both states (#12209): the nav widens from the rail to the
  // panel and back around it, so the ring stays where the rail shows it and
  // the words beside it are hidden while collapsed (the tooltip carries
  // them there; focusable, so a keyboard can reach it). On the rail the
  // anchored box is only the ring's width, so the tooltip sits beside the
  // ring rather than at the far edge of the panel-wide row the rail clips
  // (#14182); the hairline lives on the wrapper and still spans the column.
  // The column scrolls as a whole; nothing under /agents overflows it today
  // (no drawer history there), so the foot is always in view without being
  // pinned.
  return (
    <div className="flex-none border-t border-white/10 pt-3">
      <Tooltip title={tip} placement={collapsed ? "right" : "top"}>
        <div
          role="img"
          aria-label={accessibleName}
          tabIndex={0}
          data-agent-credit-foot={collapsed ? "rail" : "expanded"}
          className={`flex items-center gap-2 px-3 text-sm font-medium outline-none focus-visible:ring-1 focus-visible:ring-white/40 max-md:text-[16px] ${collapsed ? "w-10" : "w-full"} ${textColor}`}
        >
          {ring}
          <span className={`whitespace-nowrap ${collapsed ? "invisible" : ""}`}>
            {label}
          </span>
        </div>
      </Tooltip>
    </div>
  );
}

export default AgentCreditFoot;
